// @ts-nocheck
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward"; // Store Custom Code
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { imageStyles } from "@store/Themes/common/mixins/image";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import updateCssValue, { Operators } from "@toolkit/Framework/Common/Utilities/updateCssValue";
import { focusStyles, gridGapStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { IconPresentationProps } from "@insite/mobius/Icon";
import ArrowForward, { ArrowForwardOffset } from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
/* Store Custom Code - End */

export interface CategoryListProps {
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    noResult?: TypographyPresentationProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    moreGridItem?: GridItemProps;
    moreButton?: ButtonPresentationProps;
    itemLink?: LinkPresentationProps;
    itemImageWrapper?: InjectableCss;
    itemImage?: LazyImageProps;
    itemTitle?: TypographyPresentationProps;
    itemList?: TypographyProps;
    itemListItem?: TypographyProps;
    itemListLink?: LinkPresentationProps;
    itemListMoreLink?: LinkPresentationProps;
    // Store Custom Code
    itemWrapper?: InjectableCss;
    itemTitleIcon?: IconPresentationProps;
}

const horizontalGap = pxToRemArray([16, null, null, null, 24]); // Store Custom Code
const verticalGap = pxToRemArray([16, null, null, null, 24]); // Store Custom Code

export const categoryListProps: CategoryListProps = {
    title: { variant: "h2" },
    loadingOverlay: { minHeight: 100 },
    noResult: typographyGetNoResultsProps(),
    gridContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(horizontalGap, verticalGap)}
            ${breakpointValues(verticalGap, ["margin-top", "margin-bottom"])}

            &:first-child {
                margin-top: 0;
            }

            &:last-child {
                margin-bottom: 0;
            }
        `,
        offsetProps: {
            as: "ul",
        },
    },
    gridItem: {
        as: "li",
        width: [12, 6, 4, 3, 2.4 as any], // Store Custom Code
        css: css`
            position: relative;
            flex-direction: column;

            &::before {
                /* Store Removed - content */
                display: none;
                position: absolute;
                left: 0;
                width: ${pxToRem(1)};
                background-color: ${getColor("common.border")};

                ${breakpointValues(
                    verticalGap.map(x => updateCssValue(x, Operators.Divide, 2)),
                    ["top", "bottom"],
                )}
            }

            ${breakpoint("sm")} {
                &::before {
                    display: block;
                }
            }

            ${breakpoint("sm", "only")} {
                &:nth-child(2n + 1)::before {
                    display: none;
                }
            }

            ${breakpoint("md", "only")} {
                &:nth-child(3n + 1)::before {
                    display: none;
                }
            }

            ${breakpoint("lg")} {
                &:nth-child(4n + 1)::before {
                    display: none;
                }
            }
        `,
    },
    moreGridItem: {
        as: "li",
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    moreButton: {
        variant: "tertiary",
        css: css`
            ${x => x.theme.button.tertiary.css}
            width: 100%;

            ${breakpoint("md")} {
                width: auto;
            }
        `,
    },
    itemLink: {
        typographyProps: {
            css: css`
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
            `,
        },
        css: css`
            ${focusStyles()}
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            background: ${getColor("common.accent") /* Store Custom Code */};
            /* Store Removed - border-bottom */
            color: ${getColor("text.linkAlt")};
            transition: ${transitionHoverStyles("color", "border-color")};
            /* Store Custom Code - Start */
            position: relative;
            overflow: hidden;
            /* Store Custom Code - End */

            &:hover,
            &:active {
                /* Store Custom Code - Start */
                .category-name {
                    color: ${getColor("primary.contrast")};
                    background: ${getColor("primary")};
                }
                /* Store Custom Code - End */
            }
        `,
    },
    itemImageWrapper: {
        css: css`
            ${x => imageStyles(x.theme.design.category.image.small.ratio)}
            max-height: none; /* Store Custom Code */
            /* Store Custom Code - Start */
            background-position: center;
            background-size: cover;
            transition: ${transitionHoverStyles("transform")};
            margin: ${pxToRem(16)};
            mix-blend-mode: multiply;
            /* Store Custom Code - End */
        `,
    },
    itemImage: {
        css: css`
            ${x => x.theme.lazyImage.defaultProps?.css}
            background-color: transparent;
        `,
    },
    itemTitle: {
        forwardAs: "h2",
        css: css`
            /* Store Removed - spacingStyles */
            /* Store Removed - padding-bottom */
            flex-grow: 1;
            display: flex;
            align-items: center; /* Store Custom Code */
            justify-content: center; /* Store Custom Code */
            text-align: center; /* Store Custom Code */
            /* Store Custom Code - Start */
            position: absolute;
            right: ${pxToRem(16)};
            bottom: ${pxToRem(8)};
            left: ${pxToRem(16)};
            padding: ${pxToRem(13)};
            background-color: ${getColor("common.background")};
            transition: ${transitionHoverStyles("color", "background-color")};
            /* Store Custom Code - End */

            ${({ theme }) => css`
                font-size: ${theme.design.typography.size.smaller /* Store Custom Code */};
                font-weight: ${theme.design.typography.weight.bold};
                line-height: ${theme.design.typography.lineHeight.semi};
            `}
        `,
    },
    itemList: {
        as: "ul",
        css: css`
            width: 100%;
        `,
    },
    itemListItem: {
        as: "li",
    },
    itemListLink: {
        icon: {
            position: "right",
            iconProps: {
                src: ArrowForward,
                size: 20 /* Store Custom Code */,
                css: css`
                    color: inherit; /* Store Custom Code */

                    /* Store Removed - &:hover */
                `,
            },
        },
        css: css`
            ${focusStyles()}
            display: flex;
            justify-content: space-between;
            padding: 0.75em 0; /* Store Custom Code */
            color: ${getColor("text.linkAlt")};
            border-bottom: ${pxToRem(1)} solid ${getColor("common.border") /* Store Custom Code */};
            transition: ${transitionHoverStyles("color", "border-color")};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
            /* Store Custom Code - Start */
            font-size: ${x => x.theme.design.typography.size.smaller};
            /* Store Custom Code - End */

            &:hover,
            &:active {
                color: ${getColor("text.linkAltHover")};
                border-color: ${getColor("text.linkAltHover")};
            }
        `,
    },
    itemListMoreLink: {
        css: css`
            ${linkActionStyles()}
            margin-top: ${pxToRem(16) /* Store Custom Code */};
        `,
    },
    /* Store Custom Code - Start */
    itemWrapper: {
        css: css`
            display: block;
            width: 100%;
            height: 100%;
        `,
    },
    itemTitleIcon: {
        src: ArrowForward,
        css: css`
            ${iconScalableStyles(pxToRem(24), ArrowForwardOffset)};
            margin-left: ${pxToRem(8)};
            color: currentColor;
            transition: transform 0.5s ease;
        `,
    },
    /* Store Custom Code - End */
};
